import { Center, Col, VerticalCenter } from "@/common/css";
import { motion } from "framer-motion";
import Button from "@/common/ui/Button";
import { InfoText, LightDarkText, LightText, LightText2 } from "@/common/ui/Text";
import { getUploadUrls } from "@/services/common/docs.services";
import { FILE_EXTENSION_ICON_MAP, FILE_VALIDATION_CONFIGS, getFileExtension, validateFile } from "@/utils/helper/fileUploadValidation";
import { toastErrorMessage, toastMessage } from "@/utils/helper/myToast";
import { formatLengthyName } from "@/utils/helper/string";
import axios from "axios";
import { Trash2 } from "lucide-react";
import React, { useRef, useState } from "react";
import Icon from "@/common/ui/Image";
import { addEmployeeAttachment } from "@/services/employee.services";
import { useParams } from "react-router-dom";

interface AddAttachmentsProps {
  onClose: (props?: any) => void;
}
export const getFileSize = (file: File): string => {
  const bytes = file.size;
  const units = ["B", "KB", "MB", "GB"];
  let index = 0;
  let size = bytes;

  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index++;
  }

  return `${size.toFixed(2)}${units[index]}`;
};

const getFileType = (file: File): string => {
  return file.type || getFileExtension(file.name) || "unknown";
};

const AddAttachments: React.FC<AddAttachmentsProps> = ({ onClose }) => {
  const { employeeId } = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadingIndex, setCurrentUploadingIndex] = useState<number | null>(null);
  const [uploadedIndexes, setUploadedIndexes] = useState<number[]>([]);
  const [error, setError] = useState("");
  const isUploading = uploadProgress > 0 && uploadProgress < 100;
  const selectedFileExtensions: any[] = selectedFiles.map((f) => getFileExtension(f.name || ""));

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (error) setError("");
    const validFiles: File[] = [];
    for (const file of files) {
      const validationResult = validateFile(file, "image-and-pdf");
      if (validationResult) {
        setError(validationResult);
        continue;
      }
      validFiles.push(file);
    }
    setSelectedFiles((prev) => {
      const allFiles = [...prev, ...validFiles];
      if (allFiles.length > 10) {
        toastErrorMessage("You can only select up to 10 files. Only the first 10 will be used.");
        return allFiles.slice(0, 10);
      }
      return allFiles;
    });
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (error) setError("");
    const files = Array.from(event.dataTransfer.files);
    const validFiles: File[] = [];
    for (const file of files) {
      const validationResult = validateFile(file, "pdf");
      if (validationResult) {
        setError(validationResult);
        continue;
      }
      validFiles.push(file);
    }
    setSelectedFiles((prev) => {
      const allFiles = [...prev, ...validFiles];
      if (allFiles.length > 10) {
        toastErrorMessage("You can only select up to 10 files. Only the first 10 will be used.");
        return allFiles.slice(0, 10);
      }
      return allFiles;
    });
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (error) setError("");
    event.preventDefault();
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    try {
      setUploadProgress(1);
      const uploadedFiles: any[] = [];
      for (let idx = 0; idx < selectedFiles.length; idx++) {
        const file = selectedFiles[idx];
        setCurrentUploadingIndex(idx);
        //Get pre-signed URL
        const response: any = await getUploadUrls({
          fileName: file.name,
          fileType: file.type,
          uploadKey: "emp-attachment",
        });
        const { preSignedUrl, objectKey } = response.data?.data || {};

        // Upload file to the pre-signed URL
        await axios.put(preSignedUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
          onUploadProgress: (progressEvent: any) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total) - 1;
            setUploadProgress(percentCompleted);
            // console.log("uploading===>", percentCompleted, "%");
          },
        });
        uploadedFiles.push({
          name: file?.name,
          path: objectKey,
          size: getFileSize(file),
          type: getFileType(file),
        });
        setUploadedIndexes((prev) => [...prev, idx]);
      }
      setCurrentUploadingIndex(null);
      const res: any = await addEmployeeAttachment({ empId: employeeId, docs: uploadedFiles });
      if (res?.status === 200) {
        const { message, isError, showMessage } = res.data;
        if (showMessage) toastMessage(message, isError);
      } else {
        const { message, isError } = res?.response?.data || {};
        if (message) {
          toastMessage(message, isError);
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toastErrorMessage("Failed to upload file.");
    } finally {
      setUploadProgress(100);
      setCurrentUploadingIndex(null);
      onClose();
    }
  };

  const handleBrowseClick = () => {
    if (selectedFiles.length) return handleUpload();
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <VerticalCenter className="w-full h-[calc(100%-70px)] justify-between">
      <div className="w-full px-5 overflow-y-auto">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept={FILE_VALIDATION_CONFIGS["image-and-pdf"].allowedTypes.join(",")}
          className="hidden"
          multiple
        />
        {error && <InfoText text={error} className="my-4" />}
        <div className="pt-5 pb-4 sticky top-0 bg-white z-[100]">
          <div
            className="border-text-primary border  rounded-llg border-dashed min-h-40 bg-brand-light max-h-[calc(100vh-100px)]"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <Col className="py-4 px-25 gap-y-2 items-center">
              <LightDarkText text="Upload your file" className="pb-2 border-b-[1px] border-text-light3" />
              <LightDarkText text="Drag and drop your attachments here" />
              <LightText text="Your file needs to be in JPEG, PNG,PDF format with max size up to 10MB." className="!text-10 text-center" />
              <Button className="!text-13" onClick={handleBrowseClick} disabled={selectedFiles.length > 0}>
                {isUploading ? "Uploading.." : "Browse attachments"}
              </Button>
            </Col>
          </div>
        </div>
        {selectedFiles.length > 0 &&
          selectedFiles.map((file, idx) => {
            return (
              <>
                <div key={file.name + idx} className="mt-4 p-3 bg-primaryOpacity-5 rounded flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon src={selectedFileExtensions[idx] ? FILE_EXTENSION_ICON_MAP[selectedFileExtensions[idx]] : ""} alt="" className="size-6" />
                    <LightText2 text={formatLengthyName(file.name)} className="line-clamp-1" />
                    {isUploading && currentUploadingIndex === idx ? (
                      <div className="px-0.5">
                        <motion.div
                          className="w-4 h-4 rounded-full"
                          style={{
                            background: "conic-gradient(from 0deg, #FDF7FF, #FDF7FF, #FDF7FF, #C59EF7, #9A6AD4, #6F36BC)",
                            maskImage: "radial-gradient(transparent 50%, black 50%)",
                            WebkitMaskImage: "radial-gradient(transparent 50%, black 50%)",
                          }}
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      </div>
                    ) : uploadedIndexes.includes(idx) ? (
                      <Icon src="/icons/verified.svg" alt="check-icon" />
                    ) : null}
                  </div>
                  <Center className="gap-x-1">
                    <LightText text={(file.size / (1024 * 1024)).toFixed(2) + "MB"} />
                    <button onClick={() => handleRemoveFile(idx)} className=" hover:scale-103" disabled={uploadedIndexes.includes(idx) || isUploading}>
                      <Trash2 className=" text-text-default size-3 hover:scale-103" />
                    </button>
                  </Center>
                </div>
                {isUploading && currentUploadingIndex === idx && (
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-0.5">
                      <div className=" bg-text-primary/50 h-0.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}
              </>
            );
          })}
      </div>
      <div className="flex gap-5 w-full justify-end mt-4 px-5">
        <Button variant={"outline"} onClick={onClose} size="small">
          Cancel
        </Button>

        <Button onClick={handleBrowseClick} variant={"primary"} size="small" disabled={!selectedFiles.length || isUploading} isLoading={isUploading}>
          Upload
        </Button>
      </div>
    </VerticalCenter>
  );
};

export default AddAttachments;
